import { useState, useCallback } from 'react';
import { chemoTranslations, CHEMO_DEFAULTS } from './chemoTranslations';
import {
    SERVER_VALUE_YES,
    SERVER_VALUE_YES_LOWERCASE,
    SERVER_VALUE_NO_LOWERCASE,
    EMPTY_STRING,
    FINISHED_STATUS_HEB,
    COUNT_PLACEHOLDER,
    ISO_DATE_SEPARATOR,
    DATE_REGEX_ISO,
    DATE_REGEX_ISO_PREFIX,
    PAD_LENGTH,
    PAD_CHAR,
    HOUR_ZERO,
    MINUTE_ZERO,
    SECOND_ZERO,
    MILLISECOND_ZERO,
    MONTH_OFFSET,
    DATE_PART_INDEX,
    FIELD_NAMES
} from './chemoConstants';

const getTodayDate = () => {
    return new Date().toISOString().split(ISO_DATE_SEPARATOR)[DATE_PART_INDEX];
};

const getDaysFromNow = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toISOString().split(ISO_DATE_SEPARATOR)[DATE_PART_INDEX];
};

export const convertToBoolean = (value) => {
    if (typeof value === 'boolean') return value;
    if (value === SERVER_VALUE_YES || value === SERVER_VALUE_YES_LOWERCASE) return true;
    if (value === SERVER_VALUE_NO_LOWERCASE) return false;
    return false;
};

export const formatDateForInput = (dateString) => {
    if (!dateString || dateString === FINISHED_STATUS_HEB) return dateString;
    try {
        if (DATE_REGEX_ISO.test(dateString)) {
            return dateString;
        }
        const match = dateString.match(DATE_REGEX_ISO_PREFIX);
        if (match) {
            return match[DATE_PART_INDEX + MONTH_OFFSET];
        }
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
            const year = date.getUTCFullYear();
            const month = String(date.getUTCMonth() + MONTH_OFFSET).padStart(PAD_LENGTH, PAD_CHAR);
            const day = String(date.getUTCDate()).padStart(PAD_LENGTH, PAD_CHAR);
            return `${year}-${month}-${day}`;
        }
        return dateString;
    } catch {
        return dateString;
    }
};

export const useChemoReportForm = (report, hospital, fetchedRecords, onSubmit, onSave) => {
    const [formData, setFormData] = useState({
        startDate: report?.startDate || getTodayDate(),
        endDate: report?.endDate || getDaysFromNow(CHEMO_DEFAULTS.DAYS_AHEAD),
        treatmentType: report?.treatmentType || CHEMO_DEFAULTS.TREATMENT_TYPE,
        productCode: report?.productCode || CHEMO_DEFAULTS.PRODUCT_CODE,
        hospital: hospital || EMPTY_STRING
    });

    const [warningDialog, setWarningDialog] = useState({
        open: false,
        unmodifiedRecords: []
    });

    const [dateWarningDialog, setDateWarningDialog] = useState({
        open: false,
        differentDates: []
    });

    const handleFormChange = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleSubmit = useCallback(() => {
        onSubmit(formData);
    }, [formData, onSubmit]);

    const hasRecordChanged = useCallback((record) => {
        return (
            convertToBoolean(record[FIELD_NAMES.APPROVED]) === true ||
            convertToBoolean(record[FIELD_NAMES.NOT_APPROVED]) === true ||
            convertToBoolean(record[FIELD_NAMES.FINISH]) === true ||
            (record[FIELD_NAMES.TO_DUE_DATE] && record[FIELD_NAMES.TO_DUE_DATE].trim() !== EMPTY_STRING)
        );
    }, []);

    const isRecordLocked = useCallback((record) => {
        if (record[FIELD_NAMES.LOADED] === SERVER_VALUE_YES) {
            return { locked: true, reason: chemoTranslations.lock.recordLoaded };
        }

        if (record[FIELD_NAMES.DUEDATE1]) {
            const dueDate1 = new Date(record[FIELD_NAMES.DUEDATE1]);
            dueDate1.setHours(HOUR_ZERO, MINUTE_ZERO, SECOND_ZERO, MILLISECOND_ZERO);

            const startDate = new Date(formData.startDate);
            startDate.setHours(HOUR_ZERO, MINUTE_ZERO, SECOND_ZERO, MILLISECOND_ZERO);

            const endDate = new Date(formData.endDate);
            endDate.setHours(HOUR_ZERO, MINUTE_ZERO, SECOND_ZERO, MILLISECOND_ZERO);

            if (dueDate1 < startDate || dueDate1 > endDate) {
                return { locked: true, reason: chemoTranslations.lock.dateOutOfRange };
            }
        }

        return { locked: false, reason: EMPTY_STRING };
    }, [formData.startDate, formData.endDate]);

    const getPreferredDueDate = useCallback((record) => {
        return record[FIELD_NAMES.DUEDATE1] || record[FIELD_NAMES.DUEDATE];
    }, []);

    const handleWarningClose = useCallback(() => {
        setWarningDialog({ open: false, unmodifiedRecords: [] });
    }, []);

    const handleDateWarningClose = useCallback(() => {
        setDateWarningDialog({ open: false, differentDates: [] });
    }, []);

    const handleSaveAnyway = useCallback(() => {
        handleWarningClose();
        if (onSave) {
            onSave(formData);
        }
    }, [handleWarningClose, onSave, formData]);

    const handleSaveWithDifferentDates = useCallback(() => {
        handleDateWarningClose();

        const unmodifiedRecords = fetchedRecords.filter(record => {
            const lockStatus = isRecordLocked(record);
            return !lockStatus.locked && !hasRecordChanged(record);
        });

        if (unmodifiedRecords.length > 0) {
            setWarningDialog({ open: true, unmodifiedRecords });
        } else {
            if (onSave) {
                onSave(formData);
            }
        }
    }, [handleDateWarningClose, fetchedRecords, isRecordLocked, hasRecordChanged, onSave, formData]);

    const handleSave = useCallback(() => {
        if (!onSave) return;

        const unmodifiedRecords = fetchedRecords.filter(record => {
            const lockStatus = isRecordLocked(record);
            return !lockStatus.locked && !hasRecordChanged(record);
        });

        const allDates = fetchedRecords
            .filter(record => !isRecordLocked(record).locked)
            .map(record => {
                if (record[FIELD_NAMES.TO_DUE_DATE] && record[FIELD_NAMES.TO_DUE_DATE].trim() !== EMPTY_STRING && record[FIELD_NAMES.TO_DUE_DATE] !== FINISHED_STATUS_HEB) {
                    return formatDateForInput(record[FIELD_NAMES.TO_DUE_DATE]);
                }
                if (record[FIELD_NAMES.DUEDATE]) {
                    return formatDateForInput(record[FIELD_NAMES.DUEDATE]);
                }
                return null;
            })
            .filter(date => date !== null);

        const uniqueDates = [...new Set(allDates)];
        const hasDifferentDates = uniqueDates.length > 1;

        if (hasDifferentDates) {
            setDateWarningDialog({ open: true, differentDates: uniqueDates });
            return;
        }

        if (unmodifiedRecords.length > 0) {
            setWarningDialog({ open: true, unmodifiedRecords });
        } else {
            onSave(formData);
        }
    }, [onSave, fetchedRecords, isRecordLocked, hasRecordChanged, formData]);

    const getUnchangedRecordsMessage = useCallback((count) => {
        if (count === 1) {
            return chemoTranslations.warnings.oneUnchangedRecord;
        }
        return chemoTranslations.warnings.multipleUnchangedRecords.replace(COUNT_PLACEHOLDER, count);
    }, []);

    const getFormTitle = useCallback(() => {
        return report ? chemoTranslations.form.viewReport : chemoTranslations.form.currentChemoReport;
    }, [report]);

    return {
        formData,
        warningDialog,
        dateWarningDialog,
        handleFormChange,
        handleSubmit,
        handleSave,
        handleWarningClose,
        handleDateWarningClose,
        handleSaveAnyway,
        handleSaveWithDifferentDates,
        isRecordLocked,
        getPreferredDueDate,
        hasRecordChanged,
        getUnchangedRecordsMessage,
        getFormTitle,
        convertToBoolean,
        formatDateForInput
    };
};
